// components/BME688CompositeGraph.js - Updated for step-based structure
import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Chip from '@mui/material/Chip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TimelineIcon from '@mui/icons-material/Timeline';
import GridOnIcon from '@mui/icons-material/GridOn';
import { LineChart } from '@mui/x-charts/LineChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { useSensorMeasurements } from '../hooks/useSensorMeasurements';
import { getLineColors } from '../utils/helpers';

// Define measurement types to show
const MEASUREMENT_TYPES = [
  { key: 'temp', name: 'Temperature', unit: '°C', decimalPlaces: 2 },
  { key: 'hum', name: 'Humidity', unit: '%', decimalPlaces: 2 },
  { key: 'gas_res', name: 'Gas Resistance', unit: 'kΩ', decimalPlaces: 2 },
  { key: 'pres', name: 'Pressure', unit: 'hPa', decimalPlaces: 2 }
];

/**
 * Process BME688 data from new step-based structure
 *
 * NEW STRUCTURE: { "0": [8 sensors], "1": [8 sensors], ..., "9": [8 sensors] }
 * Each measurement has 10 steps, each step has 8 sensors
 *
 * @param {Array} measurements - Array of measurement objects
 * @param {string} viewMode - 'timeline' or 'steps'
 * @returns {Object} Processed data and metadata
 */
function processBME688Data(measurements, viewMode = 'timeline') {
  console.log('🔧 Processing BME688 measurements:', {
    count: measurements?.length,
    viewMode,
    firstMeasurement: measurements?.[0]
  });

  if (!measurements || measurements.length === 0) {
    return { processedData: [], sensors: [], steps: [] };
  }

  const sensors = new Set();
  const steps = new Set();
  const processedData = [];

  measurements.forEach((measurement) => {
    const timestamp = new Date(measurement.timestamp);
    const bme688Data = measurement.measurement_data?.bme688;

    if (!bme688Data || typeof bme688Data !== 'object') {
      console.warn('⚠️ No BME688 data in measurement:', measurement.measurement_id);
      return;
    }

    // NEW STRUCTURE: bme688Data is organized by steps (0-9)
    // Each step contains an array of sensor readings
    Object.entries(bme688Data).forEach(([stepKey, stepSensors]) => {
      if (!Array.isArray(stepSensors)) {
        console.warn('⚠️ Step data is not an array:', stepKey);
        return;
      }

      steps.add(stepKey);

      // Process each sensor in this step
      stepSensors.forEach((sensorReading) => {
        const senNum = sensorReading.sen_num ?? 'unknown';
        const measId = sensorReading.meas_id ?? senNum + 1;
        const profile = sensorReading.meas_profile || `HP-${measId}`;

        sensors.add(senNum);

        const dataPoint = {
          timestamp: timestamp.getTime(),
          timestampFormatted: timestamp.toLocaleString('cs-CZ'),
          measurementId: measurement.measurement_id,
          step: parseInt(stepKey),
          stepKey: stepKey,
          sensorNum: senNum,
          measId: measId,
          profile: profile,
          sensorLabel: `Sensor ${senNum} (${profile})`,
        };

        // Add measurement values
        if (sensorReading.temp !== undefined) dataPoint.temp = sensorReading.temp;
        if (sensorReading.hum !== undefined) dataPoint.hum = sensorReading.hum;
        if (sensorReading.gas_res !== undefined) dataPoint.gas_res = sensorReading.gas_res / 1000; // Convert to kΩ
        if (sensorReading.pres !== undefined) dataPoint.pres = sensorReading.pres;

        processedData.push(dataPoint);
      });
    });
  });

  // Sort data
  processedData.sort((a, b) => {
    if (viewMode === 'steps') {
      // For step view: sort by step, then sensor, then timestamp
      if (a.step !== b.step) return a.step - b.step;
      if (a.sensorNum !== b.sensorNum) return a.sensorNum - b.sensorNum;
      return a.timestamp - b.timestamp;
    } else {
      // For timeline view: sort by timestamp, then sensor, then step
      if (a.timestamp !== b.timestamp) return a.timestamp - b.timestamp;
      if (a.sensorNum !== b.sensorNum) return a.sensorNum - b.sensorNum;
      return a.step - b.step;
    }
  });

  console.log('✅ Processed BME688 data:', {
    totalPoints: processedData.length,
    sensors: Array.from(sensors).sort(),
    steps: Array.from(steps).sort(),
    samplePoint: processedData[0]
  });

  return {
    processedData,
    sensors: Array.from(sensors).sort((a, b) => a - b),
    steps: Array.from(steps).sort((a, b) => parseInt(a) - parseInt(b))
  };
}

/**
 * Prepare chart data for a specific measurement type
 * Can group by sensor or by step depending on view mode
 */
function prepareChartData(processedData, groupBy, measurementType, selectedItems = null) {
  if (processedData.length === 0) {
    return { series: [], xAxisData: [] };
  }

  const colors = getLineColors();
  const groupedData = {};
  const timePoints = new Set();

  // Filter data if specific items are selected
  const filteredData = selectedItems && selectedItems.length > 0
    ? processedData.filter(point =>
        groupBy === 'sensor'
          ? selectedItems.includes(point.sensorNum)
          : selectedItems.includes(point.step)
      )
    : processedData;

  // Group data
  filteredData.forEach(point => {
    if (point[measurementType.key] === undefined) return;

    const groupKey = groupBy === 'sensor' ? point.sensorNum : point.step;
    const groupLabel = groupBy === 'sensor'
      ? point.sensorLabel
      : `Step ${point.step}`;

    if (!groupedData[groupKey]) {
      groupedData[groupKey] = {
        label: groupLabel,
        points: []
      };
    }

    groupedData[groupKey].points.push({
      timestamp: point.timestamp,
      value: point[measurementType.key],
      step: point.step
    });

    timePoints.add(point.timestamp);
  });

  const sortedTimePoints = Array.from(timePoints).sort((a, b) => a - b);

  // Create series
  const series = Object.entries(groupedData).map(([key, data], index) => {
    // Sort points by timestamp
    data.points.sort((a, b) => a.timestamp - b.timestamp);

    // Map to chart data
    const chartData = sortedTimePoints.map(time => {
      const point = data.points.find(p => p.timestamp === time);
      return point ? point.value : null;
    });

    return {
      id: `${measurementType.key}_${key}`,
      label: data.label,
      color: colors[index % colors.length],
      data: chartData,
      showMark: chartData.length < 50 // Only show marks if not too many points
    };
  });

  return {
    series,
    xAxisData: sortedTimePoints
  };
}

/**
 * Calculate statistics for each group
 */
function calculateStats(processedData, groupBy, measurementType, selectedItems = null) {
  const colors = getLineColors();
  const stats = [];

  // Filter data if specific items are selected
  const filteredData = selectedItems && selectedItems.length > 0
    ? processedData.filter(point =>
        groupBy === 'sensor'
          ? selectedItems.includes(point.sensorNum)
          : selectedItems.includes(point.step)
      )
    : processedData;

  // Group data
  const grouped = {};
  filteredData.forEach(point => {
    if (point[measurementType.key] === undefined) return;

    const groupKey = groupBy === 'sensor' ? point.sensorNum : point.step;
    if (!grouped[groupKey]) {
      grouped[groupKey] = {
        label: groupBy === 'sensor' ? point.sensorLabel : `Step ${point.step}`,
        values: []
      };
    }
    grouped[groupKey].values.push(point[measurementType.key]);
  });

  // Calculate stats
  Object.entries(grouped).forEach(([key, data], index) => {
    if (data.values.length === 0) return;

    const min = Math.min(...data.values);
    const max = Math.max(...data.values);
    const avg = data.values.reduce((sum, val) => sum + val, 0) / data.values.length;
    const latest = data.values[data.values.length - 1];
    const stdDev = Math.sqrt(
      data.values.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) / data.values.length
    );

    stats.push({
      key,
      label: data.label,
      min,
      max,
      avg,
      latest,
      stdDev,
      count: data.values.length,
      color: colors[index % colors.length]
    });
  });

  return stats.sort((a, b) => {
    const aNum = parseInt(a.key);
    const bNum = parseInt(b.key);
    return isNaN(aNum) || isNaN(bNum) ? 0 : aNum - bNum;
  });
}

/**
 * Chart component for a single measurement type
 */
function MeasurementChart({ processedData, groupBy, measurementType, selectedItems }) {
  const { series, xAxisData } = useMemo(
    () => prepareChartData(processedData, groupBy, measurementType, selectedItems),
    [processedData, groupBy, measurementType, selectedItems]
  );

  const stats = useMemo(
    () => calculateStats(processedData, groupBy, measurementType, selectedItems),
    [processedData, groupBy, measurementType, selectedItems]
  );

  if (series.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No data available for {measurementType.name}
      </Alert>
    );
  }

  const formatValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return value.toFixed(measurementType.decimalPlaces);
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {measurementType.name} ({measurementType.unit})
        </Typography>

        <Box sx={{ width: '100%', height: 400, mb: 3 }}>
          <LineChart
            xAxis={[
              {
                data: xAxisData,
                scaleType: 'time',
                valueFormatter: (value) => {
                  const date = new Date(value);
                  return date.toLocaleTimeString('cs-CZ', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  });
                },
              }
            ]}
            yAxis={[
              {
                label: `${measurementType.name} (${measurementType.unit})`,
              }
            ]}
            series={series.map(s => ({
              ...s,
              curve: 'monotoneX', // Smooth curves
              connectNulls: true
            }))}
            width={undefined}
            height={350}
            margin={{ left: 80, right: 80, top: 20, bottom: 80 }}
            sx={{
              [`.${axisClasses.left} .${axisClasses.label}`]: {
                transform: 'translate(-10px, 0)',
              },
              [`.${axisClasses.bottom} .${axisClasses.label}`]: {
                transform: 'translate(0, 10px)',
              },
            }}
            slotProps={{
              legend: {
                direction: 'row',
                position: { vertical: 'bottom', horizontal: 'middle' },
                padding: 0,
              },
            }}
          />
        </Box>

        {/* Statistics Table */}
        {stats.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Statistics
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {groupBy === 'sensor' ? 'Sensor' : 'Step'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Count</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Min</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Max</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Average</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Std Dev</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Latest</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.map((stat) => (
                    <TableRow key={stat.key} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: stat.color,
                              mr: 1,
                            }}
                          />
                          {stat.label}
                        </Box>
                      </TableCell>
                      <TableCell>{stat.count}</TableCell>
                      <TableCell>{formatValue(stat.min)}</TableCell>
                      <TableCell>{formatValue(stat.max)}</TableCell>
                      <TableCell>{formatValue(stat.avg)}</TableCell>
                      <TableCell>{formatValue(stat.stdDev)}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {formatValue(stat.latest)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Main BME688 Composite Graph Component
 */
export default function BME688CompositeGraph({ boxId, sensorName, onBack }) {
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' or 'steps'
  const [groupBy, setGroupBy] = useState('sensor'); // 'sensor' or 'step'
  const [selectedSensors, setSelectedSensors] = useState([]); // Empty = show all
  const [selectedSteps, setSelectedSteps] = useState([]); // Empty = show all

  const { measurements, loading, error } = useSensorMeasurements(boxId, 'bme688');

  // Process data
  const { processedData, sensors, steps } = useMemo(() => {
    if (!measurements || !Array.isArray(measurements)) {
      return { processedData: [], sensors: [], steps: [] };
    }
    return processBME688Data(measurements, viewMode);
  }, [measurements, viewMode]);

  // Handle view mode change
  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      // Reset selections when changing view mode
      setSelectedSensors([]);
      setSelectedSteps([]);
    }
  };

  // Handle group by change
  const handleGroupByChange = (event, newGroupBy) => {
    if (newGroupBy !== null) {
      setGroupBy(newGroupBy);
    }
  };

  // Toggle sensor selection
  const toggleSensor = (sensorNum) => {
    setSelectedSensors(prev =>
      prev.includes(sensorNum)
        ? prev.filter(s => s !== sensorNum)
        : [...prev, sensorNum]
    );
  };

  // Toggle step selection
  const toggleStep = (step) => {
    setSelectedSteps(prev =>
      prev.includes(step)
        ? prev.filter(s => s !== step)
        : [...prev, step]
    );
  };

  const selectedItems = groupBy === 'sensor' ? selectedSensors : selectedSteps;

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Loading BME688 data...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <IconButton onClick={onBack} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                BME688 Composite Graph
              </Typography>
            </Box>
            <Alert severity="error">
              Error loading data: {error.message}
            </Alert>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // No data
  if (!measurements || measurements.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <IconButton onClick={onBack} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                BME688 Composite Graph
              </Typography>
            </Box>
            <Alert severity="info">
              No measurement data available for this sensor.
            </Alert>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'grey.50', p: 3 }}>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={onBack} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  BME688 Composite Graph
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Box: {boxId} • {measurements.length} measurements • {processedData.length} data points
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Controls */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 3 }}>
            {/* View Mode Toggle */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                View Mode
              </Typography>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
              >
                <ToggleButton value="timeline">
                  <TimelineIcon sx={{ mr: 1 }} />
                  Timeline
                </ToggleButton>
                <ToggleButton value="steps">
                  <GridOnIcon sx={{ mr: 1 }} />
                  Steps
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Group By Toggle */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Group By
              </Typography>
              <ToggleButtonGroup
                value={groupBy}
                exclusive
                onChange={handleGroupByChange}
                size="small"
              >
                <ToggleButton value="sensor">
                  By Sensor ({sensors.length})
                </ToggleButton>
                <ToggleButton value="step">
                  By Step ({steps.length})
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* Sensor/Step Filter Chips */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Filter {groupBy === 'sensor' ? 'Sensors' : 'Steps'} (click to toggle, empty = show all)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {groupBy === 'sensor' ? (
                sensors.map(sensorNum => (
                  <Chip
                    key={sensorNum}
                    label={`Sensor ${sensorNum}`}
                    onClick={() => toggleSensor(sensorNum)}
                    color={selectedSensors.length === 0 || selectedSensors.includes(sensorNum) ? 'primary' : 'default'}
                    variant={selectedSensors.length === 0 || selectedSensors.includes(sensorNum) ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))
              ) : (
                steps.map(step => (
                  <Chip
                    key={step}
                    label={`Step ${step}`}
                    onClick={() => toggleStep(parseInt(step))}
                    color={selectedSteps.length === 0 || selectedSteps.includes(parseInt(step)) ? 'primary' : 'default'}
                    variant={selectedSteps.length === 0 || selectedSteps.includes(parseInt(step)) ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Charts */}
      {processedData.length === 0 ? (
        <Card>
          <CardContent>
            <Alert severity="warning">
              No valid BME688 data found for charting.
            </Alert>
          </CardContent>
        </Card>
      ) : (
        MEASUREMENT_TYPES.map((measurementType) => (
          <MeasurementChart
            key={measurementType.key}
            processedData={processedData}
            groupBy={groupBy}
            measurementType={measurementType}
            selectedItems={selectedItems}
          />
        ))
      )}
    </Box>
  );
}